import { api } from "./client";

/**
 * The send history of one submission's two outbound messages.
 *
 * <p>A module of its own rather than more surface on `buyers.ts`: the send
 * history is read by exactly two tabs and nothing else, and `buyers.ts` is
 * already 1,500 lines carrying every buyer, listing and submission contract in
 * the module.</p>
 */

/**
 * Which of the two sends a version records.
 *
 * 🔴 These are NOT interchangeable. `IntroEmail` goes to the BUYER and moves no
 * entitlement count; `Package` goes to the LISTING BROKER and its FIRST send is
 * what moves `PackageSentAtUtc`, the column the specialist entitlement engine
 * counts; `PackageToBuyer` carries the SAME document to the BUYER and, like the
 * intro email, moves nothing. A surface that treated a version of one kind as
 * another would be offering to re-send the wrong message to the wrong person.
 */
export type BuyerSendKind =
  | "IntroEmail"
  | "Package"
  | "PackageToBuyer"
  | "BrokerIntro"
  // The Broker intro tab's other channels (R14): the text, the web form and
  // the two follow-ups. Each is recorded by the consultant (or, for the
  // follow-up email, sent from Phoenix) and moves nothing on the buyer.
  | "BrokerText"
  | "BrokerWebForm"
  | "BrokerEmailFU"
  | "BrokerTextFU";

export interface BuyerSendVersionDto {
  id: string;
  kind: BuyerSendKind;
  versionNumber: number;
  sentAtUtc: string;
  sentByUserId: string | null;
  sentByDisplayName: string | null;

  /** Null on a version carried in from before this table existed — render it as
   *  "not recorded", never as an empty address. */
  toEmail: string | null;
  subject: string | null;

  /** The fact the table exists to stop losing. Always present. */
  body: string;

  /**
   * `true` = the specialist sent it from their own mailbox; `false` = Phoenix
   * sent it.
   *
   * 🔴 `null` means NOT RECORDED — never "sent by Phoenix". Every carried-in
   * version has one, because the path was never stored.
   */
  selfAttested: boolean | null;

  /** Carried in from the single-slot body column that preceded versioning, so
   *  its body is true and almost nothing else about it is known. */
  recordedBeforeVersioning: boolean;

  /** Which prequalification letter the package enclosed. Package sends only. */
  prequalificationLetterSubmissionId: string | null;

  attachmentNames: string[];

  /** The package PDF this send carried. Null on every intro email (there is no
   *  package) and on any version recorded before the column existed — render
   *  that as "not recorded", never as a mismatch with the current package. */
  packageContentHash: string | null;

  /** The combined intro email this send was part of, or null for a
   *  single-listing send. */
  combinedIntroEmailId?: string | null;
  /** How many listings that combined email introduced, this one included.
   *  Null exactly when `combinedIntroEmailId` is. */
  combinedListingCount?: number | null;
  /** The locked listing-page section the email carried — already part of
   *  `body` — or null when it carried none. */
  listingPagesSection?: string | null;
  /** Who the message was WRITTEN AS (R13) — set on every send a person made
   *  from their own phone, mailbox or a web form and recorded; null on a
   *  Phoenix send. Distinct from `sentByUserId`, whoever clicked. */
  writtenAsUserId?: string | null;
  writtenAsDisplayName?: string | null;
}

export interface BuyerSendHistoryDto {
  introEmails: BuyerSendVersionDto[];
  packages: BuyerSendVersionDto[];
  /** The same document as `packages`, sent to the buyer. Kept apart because they
   *  are two independently-tracked sends, and because only `packages` bears on
   *  what a specialist has earned. */
  packagesToBuyer: BuyerSendVersionDto[];
  /** The short introduction of the buyer sent to the listing broker, asking
   *  whether they want the package. It comes BEFORE `packages` and is tracked
   *  separately: a submission holding one of these and no package is the
   *  ordinary case, not an anomaly. */
  brokerIntros: BuyerSendVersionDto[];
  /** The Broker intro tab's text, web form and follow-ups (R14). Always sent
   *  by the server; optional here so an older payload still types. */
  brokerTexts?: BuyerSendVersionDto[];
  brokerWebForms?: BuyerSendVersionDto[];
  brokerFollowUpEmails?: BuyerSendVersionDto[];
  brokerFollowUpTexts?: BuyerSendVersionDto[];
}

export const buyerSendVersionsApi = {
  /** One read serves every surface — they sit on the same record and a
   *  specialist moves between them. */
  history: (submissionId: string) =>
    api.get<BuyerSendHistoryDto>(`/buyer-submissions/${submissionId}/send-history`),
};
